import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PButton, PHeading, PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { DocumentResponse } from "@slfinrtl/ui-bff-routes"
import { generateTyreLabelsRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useEffect, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { commonMessages } from "src/common/messages/common.messages"
import { openPdf } from "src/common/pdf/openPdf"
import { ButtonGroup, FormLayout, ValidationMessage } from "src/components"
import { RadioGroup } from "src/components/form/radio/radioGroup/RadioGroup"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBffDynamic } from "src/hooks/useBffDynamic/useBffDynamic"
import { useLanguageOptions } from "src/hooks/useLanguageOptions/useLanguageOptions"

const messages = defineMessages({
	headline: {
		defaultMessage: "Print tyre labels",
		id: "printTyreLabels.modal.headline",
	},
	languageHeadline: {
		defaultMessage: "Choose Language",
		id: "printTyreLabels.modal.language.headline",
	},
	languageSubheadline: {
		defaultMessage: "Choose the language in which you want to print the documents.",
		id: "printTyreLabels.modal.language.subheadline",
	},
})

type Props = ModalProps<PrintTyreLabelModalContainerProps>

export interface PrintTyreLabelModalContainerProps {
	vehicleKey: string
}

export const PrintTyreLabelModalContainer = withErrorHandler(({ id, modalProps: { vehicleKey } }: Props) => {
	const { locale } = useDealerContext()
	const [selectedLocale, setSelectedLocale] = useState<Locale>(locale)
	const [isPrinting, setIsPrinting] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string | undefined>()

	const { hideModal } = useContext(ModalContext)

	const { formatMessage } = useIntl()

	const printTyreLabels = useBffDynamic(generateTyreLabelsRoute, "inline")

	const hide = useCallback(() => hideModal(id), [id, hideModal])

	const handlePrintSuccess = useCallback(
		({ document }: DocumentResponse) => {
			openPdf(document)
			hide()
		},
		[hide],
	)

	const handleError = useCallback(
		(error: any) =>
			setErrorMessage(
				error?.response?.data?.message ?? error.message ?? formatMessage(formMessages.errorGeneral),
			),
		[formatMessage],
	)
	const onSubmit = useCallback(() => {
		if (!selectedLocale) {
			return
		}

		setIsPrinting(true)
		setErrorMessage(undefined)
		printTyreLabels({
			body: undefined,
			pathParams: {
				inventoryId: vehicleKey,
				languageTag: selectedLocale,
			},
			queryParams: {},
		})
			.then(handlePrintSuccess)
			.catch(handleError)
			.finally(() => setIsPrinting(false))
	}, [selectedLocale, printTyreLabels, vehicleKey, handlePrintSuccess, handleError])

	const languageOptions = useLanguageOptions()

	const onLanguageSelected = useCallback((language: string) => setSelectedLocale(language as Locale), [])

	useEffect(() => {
		// immediately print if there's nothing to choose
		if (languageOptions.length === 1) {
			onSubmit()
		}
	})

	return (
		<Modal id={id} heading={formatMessage(messages.headline)}>
			<StyledErrorNotificationInline />
			{languageOptions.length > 1 && (
				<FormLayout>
					<div>
						<PHeading size="md">
							<FormattedMessage {...messages.languageHeadline} />
						</PHeading>
						<PText>
							<FormattedMessage {...messages.languageSubheadline} />
						</PText>
					</div>
					<RadioGroup
						name="language"
						value={selectedLocale}
						onChange={onLanguageSelected}
						required
						options={languageOptions}
					/>
				</FormLayout>
			)}

			<ButtonGroup>
				<PButton loading={isPrinting} onClick={onSubmit}>
					<FormattedMessage {...commonMessages.print} />
				</PButton>
				<PButton type="button" variant="secondary" disabled={isPrinting} onClick={hide}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
			{errorMessage && <StyledValidationMessage message={errorMessage} />}
		</Modal>
	)
})

const StyledErrorNotificationInline = styled(ErrorNotificationInline)`
	margin-top: 0;
`

const StyledValidationMessage = styled(ValidationMessage)`
	margin-top: ${spacingStaticSm};
`
